/**
 * Claim handoff URLs are credentials, not analytics dimensions. Detection is
 * semantic (route shape + query key), never a token-pattern replacement: a
 * future opaque token alphabet cannot accidentally bypass this boundary.
 */
const LOCATION_KEYS = new Set([
  "current",
  "from",
  "current_url",
  "href",
  "location",
  "pathname",
  "operation",
  "previous_pathname",
  "previous",
  "prev_pageview_pathname",
  "previous_url",
  "previousurl",
  "referrer",
  "referer",
  "to",
  "url",
]);

/** True for the property names {@link hasSensitiveLocation} treats as
 * describing THIS event's location (a credential there drops the event). */
export function isLocationKey(key: string): boolean {
  return LOCATION_KEYS.has(key.toLowerCase().replace(/^\$/, ""));
}

export function isSensitiveClaimLocation(value: unknown): boolean {
  if (typeof value !== "string" || value.trim() === "") return false;
  try {
    const url = new URL(value, "https://sill.invalid");
    const segments = url.pathname.split("/").filter(Boolean);
    return (
      (segments[0] === "marketplace" &&
        segments[1] === "claims" &&
        segments.length >= 3) ||
      (url.pathname === "/onboarding" && url.searchParams.has("claim"))
    );
  } catch {
    return false;
  }
}

/** Public-share tokens are credentials too: URLs, API paths, and error-operation
 * names must never be sent to Sentry with their opaque token segment intact. */
export function isSensitivePublicShareLocation(value: unknown): boolean {
  if (typeof value !== "string" || value.trim() === "") return false;
  try {
    const url = new URL(value, "https://sill.invalid");
    const segments = url.pathname.split("/").filter(Boolean);
    return (
      (segments[0] === "share" && segments.length >= 2) ||
      (segments[0] === "api" &&
        segments[1] === "public" &&
        segments[2] === "share" &&
        segments.length >= 4)
    );
  } catch {
    return false;
  }
}

/** CRM invite-email tokens are credentials too: the "Invite to Sill" email
 * links to the consumer `/invite/<token>` landing (one per-invitee token per
 * invites row), which trades it for the agent's code at
 * `/api/public/invites/<token>`. Neither may reach analytics or Sentry with
 * the token segment intact. */
export function isSensitiveInviteLocation(value: unknown): boolean {
  if (typeof value !== "string" || value.trim() === "") return false;
  try {
    const url = new URL(value, "https://sill.invalid");
    const segments = url.pathname.split("/").filter(Boolean);
    return (
      (segments[0] === "invite" && segments.length >= 2) ||
      (segments[0] === "api" &&
        segments[1] === "public" &&
        segments[2] === "invites" &&
        segments.length >= 4)
    );
  } catch {
    return false;
  }
}

/** Covers every URL-shaped credential route shared by Consumer and Marketing. */
export function isSensitiveLocation(value: unknown): boolean {
  return (
    isSensitiveClaimLocation(value) ||
    isSensitivePublicShareLocation(value) ||
    isSensitiveInviteLocation(value)
  );
}

/**
 * Looks only at semantically location-shaped properties. Arbitrary message or
 * DOM text is deliberately not token-scanned; callers drop the enclosing
 * automatic event when its route is sensitive.
 */
export function hasSensitiveClaimLocation(value: unknown): boolean {
  return hasSensitiveLocation(value);
}

/** Backwards-compatible claim-named export above now delegates here so shared
 * Sentry can protect public-share credential routes without special casing. */
export function hasSensitiveLocation(value: unknown): boolean {
  return hasSensitiveClaimLocationInner(value, new WeakSet<object>());
}

function hasSensitiveClaimLocationInner(
  value: unknown,
  seen: WeakSet<object>,
): boolean {
  if (!value || typeof value !== "object") return false;
  if (seen.has(value)) return false;
  seen.add(value);
  for (const [key, nested] of Object.entries(value)) {
    if (isLocationKey(key) && isSensitiveLocation(nested)) return true;
    if (
      nested &&
      typeof nested === "object" &&
      hasSensitiveClaimLocationInner(nested, seen)
    )
      return true;
  }
  return false;
}

export function currentBrowserLocation(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location?.href;
}
